'use client';


import { enqueueOperation, generateOperationId } from '@/lib/offlineQueue';

/**
 * Wraps a Supabase write operation with offline queue fallback.
 * If the operation fails due to network issues, it's queued for retry.
 * Uses upsert with onConflict:'id' for idempotency — safe to retry.
 */
export async function withOfflineQueue<T>(
  params: {
    type: 'create' | 'update' | 'delete';
    table: string;
    module: string;
    recordId: string;
    data: any;
    userId: string;
    updatedAt?: string;
  },
  operation: () => Promise<{ data: T | null; error: any }>
): Promise<{ data: T | null; error: any; queued?: boolean }> {
  // Check connectivity
  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

  if (!isOnline) {
    // Queue the operation for later
    enqueueOperation({
      type: params.type,
      table: params.table,
      module: params.module,
      recordId: params.recordId,
      data: params.data,
      userId: params.userId,
      updatedAt: params.updatedAt || new Date().toISOString(),
      maxAttempts: 8,
    });
    return { data: null, error: null, queued: true };
  }

  try {
    const result = await operation();

    // If network error (not a validation/auth error), queue it
    if (result.error) {
      const isNetworkError = !result.error.code ||
        result.error.message?.toLowerCase().includes('network') ||
        result.error.message?.toLowerCase().includes('fetch') ||
        result.error.message?.toLowerCase().includes('timeout') ||
        result.error.message?.toLowerCase().includes('connection');

      if (isNetworkError) {
        enqueueOperation({
          type: params.type,
          table: params.table,
          module: params.module,
          recordId: params.recordId,
          data: params.data,
          userId: params.userId,
          updatedAt: params.updatedAt || new Date().toISOString(),
          maxAttempts: 8,
        });
        return { data: null, error: null, queued: true };
      }
    }

    return result;
  } catch (err: any) {
    // Network exception — queue it
    enqueueOperation({
      type: params.type,
      table: params.table,
      module: params.module,
      recordId: params.recordId,
      data: params.data,
      userId: params.userId,
      updatedAt: params.updatedAt || new Date().toISOString(),
      maxAttempts: 8,
    });
    return { data: null, error: null, queued: true };
  }
}

/**
 * Generate a cryptographically safe UUID for new records.
 * This replaces the store's genId() for Supabase-bound records.
 */
export function newRecordId(): string {
  return generateOperationId();
}
